import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildShareLinkDetails,
  type ShareLinkDetailValue,
  type ShareLinkDetailsProps,
} from '../../lib/share-link-receipt';

/**
 * "Link details", web twin of `ShareLinkDetails.tsx` (audit E03): what a share
 * link shares and under which policy, from the link record the API returned.
 * Read-only. Language and revision are not on the payload yet (D06 / C10).
 */

const rowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '10px 16px',
  borderRadius: 14,
  backgroundColor: 'rgba(0,0,0,0.04)',
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-secondary)',
};

const valueStyle: CSSProperties = {
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
  color: 'var(--text-primary)',
  wordBreak: 'break-word',
};

export function ShareLinkDetails({ link, cv }: ShareLinkDetailsProps) {
  const { t, i18n } = useTranslation('common');

  if (!link) return null;

  const rows = buildShareLinkDetails(link, { cv, locale: i18n.language });
  const valueText = (value: ShareLinkDetailValue) =>
    'key' in value ? t(value.key, value.params) : value.text;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        textAlign: 'start',
      }}
    >
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text-secondary)',
        }}
      >
        {t('common:shareLinkDetails.title')}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((row) => (
          <div key={row.id} style={rowStyle}>
            <span style={labelStyle}>{t(row.labelKey)}</span>
            {row.userText ? (
              <span style={valueStyle} dir="auto">
                {valueText(row.value)}
              </span>
            ) : (
              <span style={valueStyle}>{valueText(row.value)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
